import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { fetchLiveHits } from '../../api/hitTracker';
import { fetchPitchData, parsePitchLog, parseBoxScore, parseCurrentAtBat } from '../../api/pitchByPitch';
import BoxScoreTable from './BoxScoreTable';
import PitchByPitch from './PitchByPitch';
import CurrentAtBat from './CurrentAtBat';
import RecapSummary from './RecapSummary';
import DetailedBattingLines from './DetailedBattingLines';
import PlayerProfile from '../PlayerProfile';

const { FiRefreshCw, FiList, FiActivity, FiGrid, FiFileText, FiUsers } = FiIcons;

const TABS = [
  { id: 'summary', label: 'Recap', icon: FiFileText },
  { id: 'batting', label: 'Batting Lines', icon: FiUsers },
  { id: 'boxscore', label: 'Box Score', icon: FiGrid },
  { id: 'pitches', label: 'Pitch Log', icon: FiActivity },
];

const REFRESH_INTERVAL = 12000;

const GameCenterPanel = ({ game }) => {
  const [boxData, setBoxData] = useState(null);
  const [pitches, setPitches] = useState([]);
  const [allPlays, setAllPlays] = useState([]);
  const [currentPlay, setCurrentPlay] = useState(null);
  const [linescore, setLinescore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('summary');
  const [lastUpdated, setLastUpdated] = useState(null);
  const [selectedPlayer, setSelectedPlayer] = useState(null);

  const gamePk = game?.gamePk;
  const awayAbbr = game?.teams?.away?.team?.abbreviation || 'AWY';
  const homeAbbr = game?.teams?.home?.team?.abbreviation || 'HME';
  const gameState = game?.status?.abstractGameState;
  const isLive = gameState === 'Live';

  const load = useCallback(async () => {
    if (!gamePk) return;
    try {
      const [{ playByPlay, linescore: ls }, { boxScore }] = await Promise.all([
        fetchPitchData(gamePk),
        fetchLiveHits(gamePk),
      ]);
      const plays = playByPlay?.allPlays || [];
      setPitches(parsePitchLog(plays, 50));
      setAllPlays(plays);
      setCurrentPlay(isLive ? parseCurrentAtBat(plays) : null);
      setBoxData(parseBoxScore(boxScore));
      setLinescore(ls);
      setLastUpdated(new Date());
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [gamePk, isLive]);

  useEffect(() => {
    load();
    if (isLive) {
      const id = setInterval(load, REFRESH_INTERVAL);
      return () => clearInterval(id);
    }
  }, [load, isLive]);

  if (!gamePk || gameState === 'Preview') return null;

  const gameWithPlays = { ...game, _allPlays: allPlays };

  return (
    <>
      <div className="border-2 border-[#658d71] bg-[rgba(0,0,0,0.05)] w-full">
        {/* Header */}
        <div className="p-3 sm:p-5 border-b-2 border-[#658d71] flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <SafeIcon icon={FiList} size={18} className="text-[#a9c9b1]" />
            <div>
              <h3 className="text-base sm:text-xl font-black tracking-widest text-[#d8ceb3] uppercase">
                Game Center
              </h3>
              <div className="flex items-center gap-2 mt-0.5">
                {isLive && (
                  <span className="flex items-center gap-1 text-[9px] text-[#e85555] font-black uppercase tracking-widest">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#e85555] animate-pulse" />
                    Live · Auto-refresh 12s
                  </span>
                )}
                {!isLive && lastUpdated && (
                  <span className="text-[9px] text-[#658d71] font-bold uppercase tracking-widest">
                    Final · {pitches.length} pitches logged
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {lastUpdated && (
              <span className="text-[9px] text-[#658d71] font-bold uppercase tracking-widest hidden sm:block">
                {lastUpdated.toLocaleTimeString()}
              </span>
            )}
            <button
              onClick={load}
              disabled={loading}
              className="flex items-center gap-1.5 border border-[#658d71] text-[#a9c9b1] hover:border-[#a9c9b1] hover:text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 transition-colors"
            >
              <SafeIcon icon={FiRefreshCw} size={12} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>
        </div>

        {/* Current At-Bat (live only) */}
        {isLive && currentPlay && !loading && (
          <div className="px-3 sm:px-5 pt-4">
            <CurrentAtBat currentPlay={currentPlay} linescore={linescore} />
          </div>
        )}

        {/* Tabs */}
        <div className="flex border-b-2 border-[#658d71] mt-4 overflow-x-auto">
          {TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-4 sm:px-6 py-2.5 text-[9px] sm:text-[10px] font-bold uppercase tracking-widest whitespace-nowrap border-r border-[#658d71] last:border-0 transition-colors
                ${tab === t.id
                  ? 'bg-[#386646] text-[#d8ceb3]'
                  : 'text-[#a9c9b1] hover:text-white hover:bg-[rgba(255,255,255,0.04)]'}`}
            >
              <SafeIcon icon={t.icon} size={11} />
              {t.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="p-3 sm:p-5">
          {loading ? (
            <div className="flex items-center justify-center py-14 gap-3">
              <div className="w-7 h-7 border-2 border-[#658d71] border-t-[#d8ceb3] rounded-full animate-spin" />
              <span className="text-[#a9c9b1] text-xs tracking-widest uppercase">Loading game data...</span>
            </div>
          ) : (
            <AnimatePresence mode="wait">
              <motion.div
                key={tab}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.15 }}
              >
                {tab === 'summary' && (
                  <RecapSummary
                    boxData={boxData}
                    pitches={pitches}
                    awayAbbr={awayAbbr}
                    homeAbbr={homeAbbr}
                    game={gameWithPlays}
                  />
                )}
                {tab === 'batting' && boxData && (
                  <DetailedBattingLines
                    boxData={boxData}
                    allPlays={allPlays}
                    awayAbbr={awayAbbr}
                    homeAbbr={homeAbbr}
                    onPlayerClick={(id, name) => setSelectedPlayer({ id, name })}
                  />
                )}
                {tab === 'boxscore' && boxData && (
                  <BoxScoreTable
                    boxData={boxData}
                    awayAbbr={awayAbbr}
                    homeAbbr={homeAbbr}
                    onPlayerClick={(id, name) => setSelectedPlayer({ id, name })}
                  />
                )}
                {tab === 'pitches' && (
                  <PitchByPitch pitches={pitches} isLive={isLive} />
                )}
              </motion.div>
            </AnimatePresence>
          )}
        </div>
      </div>

      {selectedPlayer && (
        <PlayerProfile
          playerId={selectedPlayer.id}
          playerName={selectedPlayer.name}
          onClose={() => setSelectedPlayer(null)}
        />
      )}
    </>
  );
};

export default GameCenterPanel;